import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { affiliationAPI } from '../services/api';
import { CheckCircle2, Clock, XCircle, RefreshCw, Printer } from 'lucide-react';

// El retorno del navegador NUNCA confirma el pago: siempre se consulta el estado real
// al backend, que sólo se marca como confirmado por el webhook firmado de Stripe.
export const AfiliacionPagoExitoPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [estado, setEstado] = useState(null);
  const [intentos, setIntentos] = useState(0);
  const [error, setError] = useState(null);

  const consultar = async () => {
    try {
      setEstado(await affiliationAPI.estadoPago(sessionId));
    } catch (e) {
      setError(e.message);
    } finally {
      setIntentos((n) => n + 1);
    }
  };

  useEffect(() => {
    if (!sessionId) return;
    consultar();
    const t = setInterval(() => {
      setIntentos((n) => {
        if (n >= 6) { clearInterval(t); return n; }
        consultar();
        return n;
      });
    }, 4000);
    return () => clearInterval(t);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmado = estado?.confirmado;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <Card className="max-w-lg w-full" data-testid="pago-exito">
        <CardContent className="p-8 text-center space-y-4">
          {confirmado ? (
            <CheckCircle2 className="h-14 w-14 text-green-600 mx-auto" />
          ) : (
            <Clock className="h-14 w-14 text-amber-500 mx-auto" />
          )}
          <h1 className="text-2xl font-bold text-gray-900">
            {confirmado ? 'Pago confirmado' : 'Pago en verificación'}
          </h1>
          <p className="text-base text-gray-600" data-testid="pago-mensaje">
            {error ? error : estado?.mensaje || 'Consultando el estado real del pago…'}
          </p>
          {estado?.folio && (
            <p className="text-sm text-gray-700">
              Folio: <strong data-testid="pago-folio">{estado.folio}</strong> · Importe:{' '}
              <strong>${estado.amount?.toLocaleString('es-MX')} {estado.currency}</strong>
            </p>
          )}
          {estado?.recibo && (
            <div className="text-left border rounded-lg p-4 bg-white space-y-1" data-testid="pago-recibo">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-gray-900">Comprobante de afiliación</h2>
                <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                  {estado.recibo.modo}
                </span>
              </div>
              <p className="text-sm text-gray-700">Folio: <strong data-testid="recibo-folio">{estado.recibo.folio}</strong></p>
              <p className="text-sm text-gray-700">
                Importe: <strong data-testid="recibo-importe">${estado.recibo.importe?.toLocaleString('es-MX')} {estado.recibo.moneda}</strong>
              </p>
              <p className="text-sm text-gray-700">Concepto: {estado.recibo.concepto}</p>
              <p className="text-sm text-gray-700" data-testid="recibo-seccion">
                Sección: {estado.recibo.seccion || 'Nacional / por asignar'}
              </p>
              <p className="text-sm text-gray-700">
                Fecha de confirmación: {estado.recibo.fecha_pago
                  ? new Date(estado.recibo.fecha_pago).toLocaleString('es-MX') : '—'}
              </p>
              <p className="text-xs text-gray-500 break-all">Referencia: {estado.recibo.referencia_stripe}</p>
              <p className="text-xs text-amber-700">{estado.recibo.sello}</p>
              <Button variant="outline" size="sm" className="mt-2"
                      onClick={() => window.print()} data-testid="recibo-imprimir">
                <Printer className="h-4 w-4 mr-2" /> Imprimir o guardar en PDF
              </Button>
            </div>
          )}
          <p className="text-xs text-gray-500">
            La confirmación proviene únicamente del webhook firmado de Stripe; esta pantalla no
            activa nada por sí sola. Tu sección revisará el expediente para completar la afiliación.
          </p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={consultar} data-testid="pago-reconsultar">
              <RefreshCw className="h-4 w-4 mr-2" /> Volver a consultar
            </Button>
            <Button asChild className="bg-gray-900"><Link to="/">Ir al inicio</Link></Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const AfiliacionPagoCanceladoPage = () => {
  const [params] = useSearchParams();
  const folio = params.get('folio');
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <Card className="max-w-lg w-full" data-testid="pago-cancelado">
        <CardContent className="p-8 text-center space-y-4">
          <XCircle className="h-14 w-14 text-red-500 mx-auto" />
          <h1 className="text-2xl font-bold text-gray-900">Pago cancelado</h1>
          <p className="text-base text-gray-600">
            No se realizó ningún cargo y tu afiliación <strong>no</strong> quedó activa.
            {folio ? ` Tu folio ${folio} sigue disponible para retomar el pago.` : ''}
          </p>
          <Button asChild className="bg-gray-900">
            <Link to="/afiliacion/solicitud">Volver a la afiliación</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AfiliacionPagoExitoPage;
